import { toast } from "react-toastify";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Box, Button, CircularProgress } from "@mui/material";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";

import { useStripePayModal } from "./useStripePayModal";

const StripePayModal = ({
  open,
  closeModal,
  clientSecret,
  publishableKey,
  queryKey,
  additionalQueryKeys,
  onSuccess,
}: {
  open: boolean;
  closeModal: () => void;
  clientSecret: string | null;
  publishableKey: string | null;
  queryKey: string[];
  additionalQueryKeys?: (string | number)[][];
  onSuccess?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  if (!clientSecret || !publishableKey) return null;

  const stripePromise = loadStripe(publishableKey);

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.container}>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: { theme: "stripe" },
            locale: i18n.language === "ar" ? "ar" : "en",
          }}
        >
          <CheckoutForm
            handleClose={closeModal}
            onLoad={() => setIsLoading(false)}
            isLoading={isLoading}
            queryKey={queryKey}
            additionalQueryKeys={additionalQueryKeys}
            onSuccess={onSuccess}
          />
        </Elements>
      </Box>
    </Modal>
  );
};

export { StripePayModal, useStripePayModal };

const CheckoutForm = ({
  handleClose,
  onLoad,
  isLoading,
  queryKey,
  additionalQueryKeys,
  onSuccess,
}: {
  handleClose: () => void;
  onLoad: () => void;
  isLoading: boolean;
  queryKey: string[];
  additionalQueryKeys?: (string | number)[][];
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "stripePayModal" });
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (result.error) {
      setIsSubmitting(false);
      // alert(result.error.message);
    } else {
      // alert("Payment successful!");
      setTimeout(() => {
        // Invalidate primary query key
        queryClient.invalidateQueries({ queryKey });
        // Invalidate additional query keys if provided
        if (additionalQueryKeys) {
          additionalQueryKeys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
        }
        toast.success(t("paymentSuccessful"));
        handleClose();
        setIsSubmitting(false);
        onSuccess?.();
      }, 1000);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
      <PaymentElement onReady={onLoad} />
      {isLoading && (
        <Box sx={styles.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoading && elements && (
        <Box sx={styles.buttonsContainer}>
          <Button type="button" variant="outlined-md" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" variant="primary-md" disabled={isLoading || isSubmitting}>
            {t("pay")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const styles = {
  container: {
    width: "800px",
    height: "fit-content",
    minHeight: "360px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    background: "#FFF",
    padding: "24px",
    boxShadow: "0px 4px 50px 0px rgba(0, 0, 0, 0.08), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
  },
  loadingContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    zIndex: 1,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "312px",
    gap: "16px",
  },
};
